import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import type { TokenUsageStats } from '../../services/api';
import { contentColumnPadding } from '../../theme/constants';
import {
  METER_COLORS,
  formatAbbrev,
  formatFull,
  formatResetCountdown,
  getDailyUsagePercent,
  getMeterState,
} from './utils';

export type PopoverPlacement = 'above' | 'below';

/** Kept in sync with `Container`'s width; TokenMeter needs it to right-align the portal. */
export const POPOVER_WIDTH = 300;

interface UsagePopoverProps {
  stats: TokenUsageStats | null;
  isDemo: boolean;
  placement: PopoverPlacement;
  onBuyClick: () => void;
  /**
   * Viewport coordinates supplied by TokenMeter. The popover is rendered
   * through a portal into document.body (see TokenMeter), so it is positioned
   * `fixed` against the viewport rather than against a relative ancestor.
   */
  style?: React.CSSProperties;
}

export const UsagePopover = React.forwardRef<HTMLDivElement, UsagePopoverProps>(
  ({ stats, isDemo, placement, onBuyClick, style }, ref) => {
    // Re-render once per minute to keep the "Resets in Xh Ym" countdown fresh.
    const [, setTick] = useState(0);
    useEffect(() => {
      const id = setInterval(() => setTick((t) => t + 1), 60_000);
      return () => clearInterval(id);
    }, []);

    if (isDemo) {
      return (
        <Container
          ref={ref}
          style={style}
          $placement={placement}
          role="dialog"
          aria-label="Token usage"
        >
          <Header>USAGE</Header>
          <Body>
            <DemoBlock>
              <DemoBadge>Unlimited</DemoBadge>
              <DemoNote>Demo accounts have no token limits.</DemoNote>
            </DemoBlock>
          </Body>
        </Container>
      );
    }

    if (!stats) {
      return (
        <Container
          ref={ref}
          style={style}
          $placement={placement}
          role="dialog"
          aria-label="Token usage"
        >
          <Header>USAGE</Header>
          <Body>
            <DemoNote>Unable to load usage information.</DemoNote>
          </Body>
        </Container>
      );
    }

    const state = getMeterState(stats, false);
    const percent = getDailyUsagePercent(stats);
    const color = METER_COLORS[state];
    const resets = formatResetCountdown(stats.daily_quota_resets_at);
    const hasPurchased = stats.remaining_purchased_tokens > 0;
    const paidMode = state === 'paid';

    return (
      <Container
        ref={ref}
        style={style}
        $placement={placement}
        role="dialog"
        aria-label="Token usage"
      >
        <Header>USAGE</Header>
        <Body>
          <Section $dimmed={paidMode}>
            <SectionHead>
              <SectionLabel>Daily free usage</SectionLabel>
              <SectionValue>
                {formatAbbrev(stats.daily_free_usage)} /{' '}
                {formatAbbrev(stats.daily_free_quota)}
              </SectionValue>
            </SectionHead>
            <Track>
              <Fill style={{ width: `${percent}%`, background: color }} />
            </Track>
            <SubText>
              {percent.toFixed(0)}% used
              {resets && (
                <>
                  {' · '}
                  Resets in {resets}
                </>
              )}
            </SubText>
          </Section>

          {hasPurchased && (
            <>
              <Divider />
              <CreditsBlock $active={paidMode}>
                <CreditsRow>
                  <CreditsIcon $active={paidMode}>+</CreditsIcon>
                  <CreditsText>
                    <CreditsTitle>
                      {formatFull(stats.remaining_purchased_tokens)} purchased tokens
                    </CreditsTitle>
                    <CreditsSub>
                      {paidMode
                        ? 'Using your purchased balance now'
                        : 'Kicks in automatically after daily free quota'}
                    </CreditsSub>
                  </CreditsText>
                </CreditsRow>
              </CreditsBlock>
            </>
          )}

          <Footer>
            <PlanChip>{(stats.plan_type || 'lite').toUpperCase()}</PlanChip>
            <BuyButton type="button" onClick={onBuyClick} $state={state}>
              {state === 'exhausted' ? 'Buy tokens' : 'Buy more tokens'}
            </BuyButton>
          </Footer>
        </Body>
      </Container>
    );
  }
);

UsagePopover.displayName = 'UsagePopover';

const popIn = keyframes`
  from { opacity: 0; transform: translateY(var(--pop-ty)); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div<{ $placement: PopoverPlacement }>`
  /* Fixed, not absolute: TokenMeter portals this into document.body so it can
     escape the ancestors that clip it. Both InputShell and FooterRow set
     overflow:hidden to contain their width animations, and the 'above'
     placement renders entirely outside FooterRow's single-row box -- so an
     absolutely positioned popover was clipped away completely, regardless of
     z-index. Offsets come from the inline style TokenMeter computes. */
  position: fixed;
  width: ${POPOVER_WIDTH}px;
  z-index: 1200;
  background: ${({ theme }) => theme.av2.surfaceElevated};
  border: 1px solid ${({ theme }) => theme.av2.primaryBorder};
  border-radius: 12px;
  box-shadow:
    0 20px 48px -12px ${({ theme }) => theme.av2.shadowWarm},
    0 8px 16px -6px ${({ theme }) => theme.av2.shadowWarm};
  backdrop-filter: blur(10px);
  --pop-ty: ${({ $placement }) => ($placement === 'above' ? '6px' : '-6px')};
  animation: ${popIn} 180ms cubic-bezier(0.22, 1, 0.36, 1);
`;

const Header = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.av2.textMuted};
  padding: 12px 16px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.av2.borderSubtle};
`;

const Body = styled.div`
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Section = styled.div<{ $dimmed?: boolean }>`
  opacity: ${({ $dimmed }) => ($dimmed ? 0.55 : 1)};
  transition: opacity 180ms ease;
`;

const SectionHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.av2.textSecondary};
  letter-spacing: 0.01em;
`;

const SectionValue = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.av2.textPrimary};
  font-variant-numeric: tabular-nums;
`;

const Track = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.av2.borderSubtle};
  border-radius: 999px;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.35);
`;

const Fill = styled.div`
  height: 100%;
  border-radius: 999px;
  transition: width 420ms cubic-bezier(0.22, 1, 0.36, 1), background 200ms ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0) 55%)'
        : 'linear-gradient(180deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0) 55%)'};
    border-radius: inherit;
    pointer-events: none;
  }
`;

const SubText = styled.div`
  margin-top: 6px;
  font-size: 10.5px;
  color: ${({ theme }) => theme.av2.textMuted};
  letter-spacing: 0.01em;
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.av2.borderSubtle};
`;

const CreditsBlock = styled.div<{ $active: boolean }>`
  border-radius: 8px;
  padding: 10px 12px;
  background: ${({ theme, $active }) =>
    $active ? theme.av2.primaryTintStrong : theme.av2.primaryTint};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.av2.primaryBorder : theme.av2.border};
`;

const CreditsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const CreditsIcon = styled.div<{ $active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme, $active }) =>
    $active ? theme.av2.primary : theme.av2.primaryLight};
  color: ${({ theme }) => theme.av2.textOnAccent};
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
`;

const CreditsText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CreditsTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.av2.textPrimary};
`;

const CreditsSub = styled.div`
  font-size: 10.5px;
  color: ${({ theme }) => theme.av2.textMuted};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 2px;
`;

const PlanChip = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: ${({ theme }) => theme.av2.textMuted};
  padding: 3px 8px;
  border: 1px solid ${({ theme }) => theme.av2.border};
  border-radius: 999px;
`;

const BuyButton = styled.button<{ $state: string }>`
  flex: 1;
  max-width: 180px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.av2.textOnAccent};
  cursor: pointer;
  border: none;
  background: ${({ theme, $state }) =>
    $state === 'exhausted'
      ? `linear-gradient(135deg, #e8774a 0%, ${theme.av2.errorBorder} 100%)`
      : `linear-gradient(135deg, ${theme.av2.primaryLight} 0%, ${theme.av2.primary} 100%)`};
  box-shadow: 0 2px 6px
    ${({ theme, $state }) =>
      $state === 'exhausted' ? 'rgba(216, 90, 48, 0.35)' : theme.av2.shadowAccent};
  transition: transform 120ms ease, box-shadow 120ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px
      ${({ theme, $state }) =>
        $state === 'exhausted' ? 'rgba(216, 90, 48, 0.45)' : theme.av2.shadowAccentStrong};
  }
  &:active {
    transform: translateY(0);
  }
`;

const DemoBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`;

const DemoBadge = styled.div`
  padding: 4px 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.av2.primaryTint};
  border: 1px solid ${({ theme }) => theme.av2.primaryBorder};
  color: ${({ theme }) => theme.av2.primary};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
`;

const DemoNote = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.av2.textMuted};
  text-align: center;
`;
